<template>
  <tasks
    :tasks="tasks"
    :operators-by-id="operatorsById"
    :operator-id="operatorId"
    :is-loading="isLoading"
    :can-assign-to-other-people="canAssignToOtherPeople"
    :can-edit-tasks="canEditTasks"
    :tag-suggestions="tagSuggestions"
    :analytics="analytics"
    @save="onSave"
    @inline-edit="onInlineEdit"
    @search="loadTasks($event, true)"
    @show-analytics="loadAnalytics"
    @bulk-update="onBulkUpdate"
    @delete="onDelete"
  />
</template>

<script>
import { keyBy } from 'lodash'
import { API } from '@/utils/api'
import Tasks from '../components/Tasks.vue'
const TASK_LIST = '/$v/task/list.json'
const TASK_UPDATE = '/$v/task/update.json'
const TASK_BULK_UPDATE = '/$v/task/bulk_update.json'
const TASK_DELETE = '/$v/task/delete.json'
const TASK_ANALYTICS = '/$v/task/analytics.json'
const USER_LIST = '/users/list_info.json'
const TAG_SUGGESTION_LIST = '/$v/task/tag_suggestions.json'

const defaultFilter = {
  from: dateRangeList[0].value.from,
  to: dateRangeList[0].value.to,
  status: ['new'],
}

export default {
  components: {
    Tasks
  },
  data() {
    return {
      isLoading: false,
      tasks: [],
      operatorsById: null,
      tagSuggestions: [],
      analytics: [],
    }
  },
  mounted() {
    this.isLoading = true

    Promise.all([
      this.loadOperators(),
      this.loadTasks(defaultFilter),
      this.canEditTasks ? this.loadTagSuggestions() : Promise.resolve(),
    ]).finally(() => this.isLoading = false)
  },
  computed: {
    operatorId() {
      return this.$store.state.auth.user.user_id
    },
    canEditTasks() {
      return this.$store.state.auth.checkAccess({key: 'task_control', value: true})
    },
    canAssignToOtherPeople() {
      return this.$store.state.auth.checkAccess({key: 'task_assignment', value: true})
    }
  },
  methods: {
    loadTasks(filter, withLoading = false) {
      if (withLoading) {
        this.isLoading = true
      }
      return API.call(TASK_LIST, filter)
        .then(r => this.tasks = r.data)
        .finally(() => {
          if (withLoading) {
            this.isLoading = false
          }
        })
    },
    loadOperators() {
      return API.call(USER_LIST).then(r => this.operatorsById = keyBy(r.data, 'user_id'))
    },

    loadTagSuggestions() {
      return API.call(TAG_SUGGESTION_LIST).then(r => this.tagSuggestions = r.data)
    },

    onInlineEdit(task, filters) {
      this.$root.$confirm('Confirm', 'Are you sure you want to edit this task?')
        .then(yes => {
          if (yes) {
            this.onSave(task, filters)
          }
        })
    },

    onSave(task, filters) {
      this.isLoading = true

      return API.call(TASK_UPDATE, {
        task
      }).then(() => this.loadTasks(filters)).finally(() => this.isLoading = false)
    },

    onDelete(id, filters) {
      return this.$root.$confirm('Confirm', 'Are you sure you want to delete this task?')
        .then(yes => {
          if (!yes) {
            return
          }

          this.isLoading = true

          return API.call(TASK_DELETE, {
            id
          }).then(() => this.loadTasks(filters)).finally(() => this.isLoading = false)
        })
    },

    loadAnalytics(args) {
      this.isLoading = true
      return API.call(TASK_ANALYTICS, args)
        .then(r => this.analytics = r.data)
        .finally(() => this.isLoading = false)
    },

    onBulkUpdate(args) {
      return this.$root.$confirm('Confirm', `Are you sure you want to edit the ${args.task_ids.length} selected tasks?`)
        .then(yes => {
          if (!yes) {
            return
          }

          this.isLoading = true
          return API.call(TASK_BULK_UPDATE, args)
            .then(r => {
              for (const updatedTask of r.data) {
                for (const task of this.tasks) {
                  if (updatedTask.task_id === task.task_id) {
                    Object.assign(task, updatedTask)
                  }
                }
              }
            })
            .finally(() => {
              this.isLoading = false
            })
        })
    }
  }
}
</script>
